import { useCallback, useMemo, useRef, useState } from "react";
import {
  queryKeys,
  useEarningsHistory,
  usePanelist,
  usePublicPanel,
  useRequestCashout,
} from "../api";
import { ApiError } from "../api/errors";
import {
  ErrorState,
  SkeletonBlock,
  StatsSkeleton,
} from "../components/ui/AsyncState";
import { Card } from "../components/ui/Card";
import { EarningRow } from "../components/ui/EarningRow";
import { FilterPills } from "../components/ui/FilterPills";
import { Page } from "../components/ui/Page";
import { StatCard } from "../components/ui/StatCard";
import type {
  Earning,
  EarningsDateRange,
  EarningsStatusFilter,
} from "../types";
import { formatNumber, walletState } from "../utils";
import { isDevelopment } from "../utils/app";
const getDataFilter = (
  statusFilter: EarningsStatusFilter,
): ((row: Earning) => boolean) => {
  switch (statusFilter) {
    case "all":
      return () => true;
    case "paid":
      return (row) => row.status === "paid";
    case "accepted":
      return (row) => row.status === "accepted";
    case "pending":
      return (row) => row.status === "pending";
    case "failed":
      return (row) =>
        row.status === "failed" ||
        row.status === "error" ||
        row.status === "timeout" ||
        row.status === "blacklist" ||
        row.status === "qa_fail";
    default:
      return () => false;
  }
};
export function EarningsHistoryPage() {
  const [dateFilter, setDateFilter] = useState<EarningsDateRange>("all");
  const [statusFilter, setStatusFilter] = useState<EarningsStatusFilter>("all");
  const dataFilter = useCallback(getDataFilter(statusFilter), [statusFilter]);
  const [isRedeemOpen, setIsRedeemOpen] = useState(false);
  const [isRedeemSent, setIsRedeemSent] = useState(false);
  const filters = { dateRange: dateFilter, status: "all" as const };
  const { data: earnings, error, isLoading } = useEarningsHistory(filters);
  const items = useMemo(
    () => earnings?.items.filter(dataFilter) ?? [],
    [earnings, dataFilter],
  );
  const redeem = useRequestCashout({
    onSuccess: async (a, b, c, ctx) => {
      setIsRedeemSent(true);
      await ctx.client.invalidateQueries({ queryKey: queryKeys.panelist });
    },
  });
  const cashoutKeyRef = useRef<string | undefined>(undefined);
  const { data: panelist } = usePanelist();
  const panelQuery = usePublicPanel();
  const balancePoints = panelist?.points ?? earnings?.summary.totalPts ?? 0;
  const wallet = walletState(panelQuery.data, balancePoints);
  const voucherEmail = panelist?.email;
  const canCashout = isDevelopment() || wallet.canCashout;

  function submitRedeem() {
    cashoutKeyRef.current ??= crypto.randomUUID();

    redeem.mutate(
      { idempotencyKey: cashoutKeyRef.current, method: "voucher" },
      {
        onError: (error) => {
          if (
            error instanceof ApiError &&
            (error.status === 400 || error.status === 422)
          ) {
            cashoutKeyRef.current = undefined;
          }
        },
        onSuccess: () => {
          cashoutKeyRef.current = undefined;
        },
      },
    );
  }

  if (isLoading) {
    return (
      <Page>
        <div className="earnings-wrap">
          <StatsSkeleton />
          <Card title="History">
            <SkeletonBlock count={4} height={42} radius={4} />
          </Card>
        </div>
      </Page>
    );
  }

  if (error || !earnings) {
    return (
      <Page>
        <ErrorState error={error} title="Could not load earnings history" />
      </Page>
    );
  }

  return (
    <Page>
      <div className="earnings-wrap">
        <Card style={{ marginBottom: 20 }}>
          <div className="earnings-balance-card">
            <div className="earnings-balance-main">
              <div className="card-title title-sm earnings-balance-label">
                Your balance
              </div>
              <div className="earnings-balance-points">
                {formatNumber(balancePoints)} pts
              </div>
              <div className="earnings-balance-subtext">
                {canCashout
                  ? "Ready to redeem!"
                  : `${formatNumber(wallet.toNext)} pts to go before you can redeem`}
              </div>
            </div>

            <button
              aria-expanded={isRedeemOpen}
              className="btn earnings-balance-button"
              disabled={!canCashout}
              onClick={() => {
                setIsRedeemOpen((open) => !open);
              }}
              type="button"
            >
              Redeem points
            </button>
          </div>

          <div className="earnings-balance-progress" aria-hidden="true">
            <div
              className="earnings-balance-progress-fill"
              style={{ width: `${wallet.pct}%` }}
            />
          </div>

          <div className="earnings-balance-footnote">
            {canCashout
              ? "Minimum threshold reached"
              : `Minimum threshold is ${formatNumber(wallet.minimum)} pts`}
          </div>

          {isRedeemOpen ? (
            <div className="earnings-redeem-panel">
              <div className="earnings-redeem-divider" />
              {isRedeemSent ? (
                <div className="earnings-redeem-success">
                  <div className="earnings-redeem-success-icon">✔</div>
                  <h3 className="earnings-redeem-success-title title-sm">
                    Cashout requested!
                  </h3>
                  <p className="earnings-redeem-success-copy">
                    Your cashout request has been sent to the admins for review.
                    Once accepted, you will receive an email with further
                    instructions. Please allow up to 24 hours for processing.
                  </p>
                  <button
                    className="btn earnings-redeem-done"
                    onClick={() => {
                      setIsRedeemOpen(false);
                      setIsRedeemSent(false);
                    }}
                    type="button"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="earnings-redeem-title title-medium title-subtle">
                    Redeem your points
                  </h3>
                  <p className="earnings-redeem-copy">
                    We&apos;ll send a voucher link to your email. The link lets
                    you choose from a range of vouchers and gift cards.
                  </p>

                  <div className="earnings-redeem-email-card">
                    <div className="earnings-redeem-email-label">
                      Sending voucher link to
                    </div>
                    <div className="earnings-redeem-email-value">
                      {voucherEmail}
                    </div>
                  </div>

                  <ErrorState error={redeem.error} />

                  <button
                    className="btn earnings-redeem-confirm btn-full"
                    onClick={submitRedeem}
                    type="button"
                    disabled={redeem.isPending || !canCashout}
                  >
                    {redeem.isPending ? "Processing..." : "Redeem now"}
                  </button>
                  <button
                    className="btn earnings-redeem-cancel btn-full"
                    onClick={() => setIsRedeemOpen(false)}
                    type="button"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          ) : null}
        </Card>

        <div
          className="stats-row"
          style={{ gridTemplateColumns: "repeat(3,1fr)", marginBottom: 20 }}
        >
          <StatCard
            label="Total earned"
            sub={`≈ ${earnings.summary.totalGbp}`}
            value={earnings.summary.totalPts}
          />
          <StatCard
            label="Pending"
            mutedSub
            sub={`≈ ${earnings.summary.pendingGbp}`}
            value={earnings.summary.pendingPts}
          />
          <StatCard
            label="Tasks done"
            mutedSub
            sub="All time"
            value={earnings.summary.tasksDone}
          />
        </div>

        <Card
          title="History"
          action={
            <select
              onChange={(event) =>
                setDateFilter(event.target.value as EarningsDateRange)
              }
              style={{
                appearance: "none",
                background:
                  "var(--white) url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%239A8E7E'/%3E%3C/svg%3E\") no-repeat right 10px center",
                border: "1.5px solid var(--cream-dk)",
                borderRadius: "var(--r)",
                color: "var(--text)",
                cursor: "pointer",
                fontFamily: "var(--font-b)",
                fontSize: 12.5,
                padding: "5px 28px 5px 10px",
              }}
              value={dateFilter}
            >
              <option value="all">All time</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 3 months</option>
            </select>
          }
        >
          <div style={{ marginBottom: 14 }}>
            <FilterPills
              onChange={(value) =>
                setStatusFilter(value as EarningsStatusFilter)
              }
              options={[
                { value: "all", label: "All" },
                { value: "paid", label: "Paid" },
                { value: "accepted", label: "Accepted" },
                { value: "pending", label: "Pending" },
                { value: "failed", label: "Failed" },
              ]}
              value={statusFilter}
            />
          </div>

          {items.length ? (
            items.map((earning) => (
              <EarningRow earning={earning} key={earning.id} />
            ))
          ) : (
            <div
              style={{
                color: "var(--sand)",
                fontSize: 13,
                padding: 32,
                textAlign: "center",
              }}
            >
              No transactions found for the selected filter.
            </div>
          )}
        </Card>
      </div>
    </Page>
  );
}
